import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { fetchMapGeocode, type MapGeocodeHit } from '@/lib/api/map';
import { CLUSTER_OUT_OF_BOUNDS_MESSAGE, isWithinEasternGalilee } from '@/lib/map/eastern-galilee';
import styles from './SettlementMapSearch.module.css';

export type MapSettlementOption = {
  id: string;
  name: string;
  lat?: number;
  lng?: number;
};

export type MapSearchFocusTarget = {
  name: string;
  lat: number;
  lng: number;
  id?: string;
};

type SearchResult =
  | (MapGeocodeHit & { id?: string; needsGeocode?: false })
  | {
      id?: string;
      name: string;
      displayName: string;
      source: 'registry';
      needsGeocode: true;
    };

export function normalizeSettlementQuery(value: string): string {
  return value
    .trim()
    .replace(/[""]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function settlementMatchesQuery(name: string, query: string): boolean {
  const n = normalizeSettlementQuery(name);
  const q = normalizeSettlementQuery(query);
  if (!q) return true;
  return n.includes(q) || q.includes(n);
}

export function localSearchResults(
  query: string,
  settlements: MapSettlementOption[],
): SearchResult[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  return settlements
    .filter((settlement) => settlementMatchesQuery(settlement.name, trimmed))
    .slice(0, 12)
    .map((settlement) => {
      if (settlement.lat != null && settlement.lng != null) {
        return {
          id: settlement.id,
          name: settlement.name,
          displayName: settlement.name,
          lat: settlement.lat,
          lng: settlement.lng,
          source: 'registry' as const,
        };
      }
      return {
        id: settlement.id,
        name: settlement.name,
        displayName: settlement.name,
        source: 'registry' as const,
        needsGeocode: true as const,
      };
    });
}

export function mergeSearchResults(
  local: SearchResult[],
  remote: MapGeocodeHit[],
): SearchResult[] {
  const seen = new Set(local.map((item) => normalizeSettlementQuery(item.name)));
  const merged: SearchResult[] = [...local];
  for (const hit of remote) {
    const key = normalizeSettlementQuery(hit.name);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(hit);
  }
  return merged.slice(0, 12);
}

export function shouldFetchRemoteSuggestions(
  query: string,
  local: SearchResult[],
): boolean {
  const trimmed = query.trim();
  if (trimmed.length < 2) return false;
  return !local.some((item) => !('needsGeocode' in item && item.needsGeocode));
}

export function resolveSettlementFromQuery(
  query: string,
  settlements: MapSettlementOption[],
): MapSettlementOption | undefined {
  const trimmed = query.trim();
  if (!trimmed) return undefined;

  const exact = settlements.find(
    (s) => normalizeSettlementQuery(s.name) === normalizeSettlementQuery(trimmed),
  );
  if (exact) return exact;

  const matches = settlements.filter((s) => settlementMatchesQuery(s.name, trimmed));
  if (matches.length === 1) return matches[0];
  return undefined;
}

export default function SettlementMapSearch({
  settlements,
  onFocus,
  selectedSettlementId,
  disabled = false,
}: {
  settlements: MapSettlementOption[];
  onFocus: (target: MapSearchFocusTarget) => void;
  selectedSettlementId?: string;
  disabled?: boolean;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const lastSyncedId = useRef<string | null>(null);
  const geocodeRequestRef = useRef(0);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [remoteHits, setRemoteHits] = useState<MapGeocodeHit[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteAttempted, setRemoteAttempted] = useState(false);
  const [menuRect, setMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const sorted = useMemo(
    () => [...settlements].sort((a, b) => a.name.localeCompare(b.name, 'he')),
    [settlements],
  );

  const localResults = useMemo(() => localSearchResults(query, sorted), [query, sorted]);
  const results = useMemo(
    () => mergeSearchResults(localResults, remoteHits),
    [localResults, remoteHits],
  );

  const updateMenuRect = useCallback(() => {
    const anchor = inputWrapRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setMenuRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    updateMenuRect();
    window.addEventListener('resize', updateMenuRect);
    window.addEventListener('scroll', updateMenuRect, true);
    return () => {
      window.removeEventListener('resize', updateMenuRect);
      window.removeEventListener('scroll', updateMenuRect, true);
    };
  }, [open, query, results.length, remoteLoading, updateMenuRect]);

  useEffect(() => {
    const trimmed = query.trim();
    setRemoteHits([]);
    setRemoteAttempted(false);
    if (!shouldFetchRemoteSuggestions(trimmed, localResults)) {
      setRemoteLoading(false);
      return;
    }

    const requestId = ++geocodeRequestRef.current;
    const timer = window.setTimeout(() => {
      setRemoteLoading(true);
      void fetchMapGeocode(trimmed, 6)
        .then(({ hits }) => {
          if (geocodeRequestRef.current !== requestId) return;
          setRemoteHits(hits);
        })
        .catch(() => {
          if (geocodeRequestRef.current !== requestId) return;
          setRemoteHits([]);
        })
        .finally(() => {
          if (geocodeRequestRef.current !== requestId) return;
          setRemoteLoading(false);
          setRemoteAttempted(true);
        });
    }, 280);

    return () => window.clearTimeout(timer);
  }, [query, localResults]);

  const geocodeQuery = useCallback(async (text: string): Promise<MapGeocodeHit | null> => {
    const { hits } = await fetchMapGeocode(text, 5);
    const exact = hits.find(
      (hit) => normalizeSettlementQuery(hit.name) === normalizeSettlementQuery(text),
    );
    return exact ?? hits[0] ?? null;
  }, []);

  const flyToResult = useCallback(
    async (result: SearchResult) => {
      if ('needsGeocode' in result && result.needsGeocode) {
        setResolving(true);
        setStatus(null);
        try {
          const hit = await geocodeQuery(result.name);
          if (!hit) {
            setStatus('לא נמצא מיקום בתחום אשכול גליל מזרחי — נסו שם אחר');
            setOpen(true);
            return;
          }
          if (!isWithinEasternGalilee(hit.lat, hit.lng)) {
            setStatus(CLUSTER_OUT_OF_BOUNDS_MESSAGE);
            setOpen(true);
            return;
          }
          setQuery(hit.name);
          setOpen(false);
          onFocus({
            id: result.id,
            name: hit.name,
            lat: hit.lat,
            lng: hit.lng,
          });
        } catch {
          setStatus('חיפוש המיקום נכשל — נסו שוב');
          setOpen(true);
        } finally {
          setResolving(false);
        }
        return;
      }

      if (!isWithinEasternGalilee(result.lat, result.lng)) {
        setStatus(CLUSTER_OUT_OF_BOUNDS_MESSAGE);
        setOpen(true);
        return;
      }
      setStatus(null);
      setQuery(result.name);
      setOpen(false);
      onFocus({
        id: result.id,
        name: result.name,
        lat: result.lat,
        lng: result.lng,
      });
    },
    [onFocus, geocodeQuery],
  );

  const submitQuery = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const local = resolveSettlementFromQuery(trimmed, sorted);
    if (local?.lat != null && local.lng != null) {
      await flyToResult({
        id: local.id,
        name: local.name,
        displayName: local.name,
        lat: local.lat,
        lng: local.lng,
        source: 'registry',
      });
      return;
    }

    if (results.length === 1) {
      await flyToResult(results[0]);
      return;
    }

    setResolving(true);
    setStatus(null);
    try {
      const hit = await geocodeQuery(trimmed);
      if (hit) {
        if (!isWithinEasternGalilee(hit.lat, hit.lng)) {
          setStatus(CLUSTER_OUT_OF_BOUNDS_MESSAGE);
          setOpen(true);
          return;
        }
        setQuery(hit.name);
        setOpen(false);
        onFocus({ name: hit.name, lat: hit.lat, lng: hit.lng });
        return;
      }
      setStatus('לא נמצא מיקום בתחום אשכול גליל מזרחי — נסו שם אחר');
      setOpen(true);
    } catch {
      setStatus('חיפוש המיקום נכשל — נסו שוב');
      setOpen(true);
    } finally {
      setResolving(false);
    }
  }, [query, sorted, results, flyToResult, geocodeQuery, onFocus]);

  useEffect(() => {
    if (!selectedSettlementId) {
      lastSyncedId.current = null;
      return;
    }
    if (lastSyncedId.current === selectedSettlementId) return;
    const match = sorted.find((s) => s.id === selectedSettlementId);
    if (!match) return;
    lastSyncedId.current = selectedSettlementId;
    setQuery(match.name);
    if (match.lat != null && match.lng != null) {
      setStatus(null);
      onFocus({ id: match.id, name: match.name, lat: match.lat, lng: match.lng });
      return;
    }
    void (async () => {
      setResolving(true);
      try {
        const hit = await geocodeQuery(match.name);
        if (hit) {
          setStatus(null);
          onFocus({ id: match.id, name: hit.name, lat: hit.lat, lng: hit.lng });
        } else {
          setStatus('לא נמצא מיקום ליישוב שנבחר — חפשו שם אחר');
        }
      } catch {
        setStatus('חיפוש המיקום נכשל — נסו שוב');
      } finally {
        setResolving(false);
      }
    })();
  }, [selectedSettlementId, sorted, onFocus, geocodeQuery]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if ((target as Element).closest?.('[data-settlement-search-menu="true"]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, results.length]);

  const menu =
    open && menuRect && query.trim().length >= 2 ? (
      <div
        data-settlement-search-menu="true"
        className={styles.menuPortal}
        style={{
          top: menuRect.top,
          left: menuRect.left,
          width: menuRect.width,
        }}
      >
        {results.length > 0 ? (
          <ul
            id={`${listId}-listbox`}
            className={styles.listbox}
            role="listbox"
            aria-label="תוצאות חיפוש מיקום"
          >
            {results.map((result, index) => (
              <li
                key={`${result.source}-${result.name}-${'id' in result ? result.id : index}`}
                role="presentation"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`${styles.option} ${index === activeIndex ? styles.optionActive : ''}`.trim()}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void flyToResult(result)}
                >
                  <span className={styles.optionName}>{result.name}</span>
                  {'needsGeocode' in result && result.needsGeocode ? (
                    <span className={styles.optionHint}>חיפוש מיקום…</span>
                  ) : 'displayName' in result &&
                    result.displayName !== result.name &&
                    result.source !== 'registry' ? (
                    <span className={styles.optionHint}>{result.displayName}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : remoteLoading ? (
          <p className={styles.empty} role="status">
            מחפש…
          </p>
        ) : remoteAttempted ? (
          <p className={styles.empty} role="status">
            לא נמצא מיקום — נסו שם אחר
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div className={styles.wrap} ref={rootRef} data-testid="map-settlement-search">
      <label className={styles.label} htmlFor={`${listId}-input`}>
        חיפוש מיקום במפה
      </label>
      <div className={styles.searchRow}>
        <div className={styles.inputWrap} ref={inputWrapRef}>
          <input
            id={`${listId}-input`}
            className={styles.input}
            type="text"
            inputMode="search"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${listId}-listbox`}
            aria-autocomplete="list"
            autoComplete="off"
            enterKeyHint="search"
            placeholder="לדוגמה: אביבים, בית הלל, כפר גלעדי…"
            disabled={disabled}
            dir="rtl"
            value={query}
            onFocus={() => {
              setOpen(true);
              updateMenuRect();
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              setStatus(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setOpen(true);
                setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
                return;
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                const pick = results[activeIndex];
                if (pick) {
                  void flyToResult(pick);
                } else {
                  void submitQuery();
                }
                return;
              }
              if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
          {query ? (
            <button
              type="button"
              className={styles.clearBtn}
              aria-label="נקה חיפוש"
              onClick={() => {
                setQuery('');
                setRemoteHits([]);
                setRemoteAttempted(false);
                setStatus(null);
                setOpen(true);
              }}
            >
              ×
            </button>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.focusBtn}
          disabled={disabled || resolving || !query.trim()}
          onClick={() => void submitQuery()}
        >
          {resolving ? '…' : 'מיקוד'}
        </button>
      </div>
      {status ? (
        <p className={styles.status} role="status">
          {status}
        </p>
      ) : null}
      {typeof document !== 'undefined' && menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
